// billingPlanContent — the EDITABLE copy for the Billing & Plans cards.
//
// ─────────────────────────────────────────────────────────────────────────────
// HOW TO EDIT  (this is the one place to change what the plan cards SAY)
// ─────────────────────────────────────────────────────────────────────────────
// `PLAN_PRESENTATION` is a plain config object keyed by plan. To change a card:
//
//   • Add / edit / remove a bullet → edit the `items` array of a group:
//         { text: 'Email Broadcasts', limit: 'Up to 5 active at once' }
//     - text  : the bullet label (required)
//     - hl    : true → visually emphasized (the tier's headline unlock)
//     - limit : small sub-line under the bullet (e.g. "Up to 5 active at once")
//     - was   : funnel contrast line (e.g. "5 on Base")
//
//   • Add / rename a feature GROUP → edit a `groups[]` entry:
//         { label: 'Marketing engine', items: [ … ] }
//     - label      : the group header (sentence case)
//     - featureKey : OPTIONAL entitlement key — see the note on PlanFeatureGroup.
//                    Leave it off unless you specifically want PlanCard to HIDE
//                    this group for an org that already holds that entitlement.
//
//   • tagline / heroLine / inheritFrom : the top-of-card editorial framing.
//
// This file is EDITORIAL ONLY. It does NOT decide which tier HAS a feature, what
// anything costs, or any fee % — those come from the entitlement engine
// (@charitystack/entitlements) via billingPlans.ts and are never hard-coded here.
// It is typed (not raw JSON) on purpose: TypeScript catches a bad plan key, a
// misspelled field, or a featureKey that isn't a real entitlement at compile
// time, instead of failing silently in production.
//
// EXCEPTION — usage-limit NUMBERS embedded in a bullet's `text` (e.g. "5,000 SMS
// / mo included"): these must NOT be hand-maintained as plain literals, because a
// literal silently drifts the next time the entitlements YAML changes (this
// happened — Pro showed a stale "4,000" bullet after the catalog moved to 5,000,
// PE-1855 QA fix 2026-07-24). Any bullet naming a usage-limit number MUST set the
// matching resolver field (e.g. `smsIncludedTier`) so resolveDynamicCopy()
// (billingPlans.ts) overwrites `text` with the live CATALOG value at render time.
// Treat the literal in `text` as a compile-time fallback/placeholder only.
//
// Consumed by PlanCard.tsx (renders the cards) via a re-export from
// billingPlans.ts — import these symbols from 'utils/billingPlans' as before.

import type { Plan } from '@charitystack/entitlements';

export type PlanFeatureItem = {
  text: string;
  hl?: boolean; // visually emphasized (the tier's headline unlock)
  limit?: string; // small sub-line, e.g. "Up to 5 active at once"
  was?: string; // funnel "was X on lower tier" contrast line
  // PocketFlows v1 ships without SMS. Two gating modes (stripSmsFromPresentation
  // applies them when the `pocketflows_sms` flag is off; both are non-destructive
  // — flip the flag and the original copy returns verbatim):
  //   - `smsGated: true` REMOVES the whole item (it only exists to sell SMS,
  //     e.g. "3,000 SMS / mo included").
  //   - `textNoSms` REWORDS the item (the feature stays; only the SMS mention
  //     drops). Set it only when `text` itself names SMS.
  smsGated?: boolean;
  textNoSms?: string;
  // Which tier's `sms_included_per_month` catalog value this item's `text`
  // embeds (e.g. "4,000 SMS / mo included"). When set, resolveDynamicCopy()
  // (billingPlans.ts) rewrites `text`'s leading number to CATALOG's live value
  // at render time, so the bullet can never drift from the entitlements engine
  // the way a plain hard-coded literal did (PE-1855 QA fix: Pro showed a stale
  // "4,000" while the engine already said 5,000). Set this on every SMS-count
  // bullet — do NOT hand-type the number in `text`; keep `text` as the
  // fallback/placeholder copy only.
  smsIncludedTier?: Plan;
};
export type PlanFeatureGroup = {
  label: string;
  items: PlanFeatureItem[];
  // Representative entitlement feature key for this group (the engine key that
  // most directly gates the group's headline capability). Used by PlanCard to
  // hide a group on an UPGRADE card when the viewing org ALREADY has it — e.g. a
  // grandfathered legacy_starter org already holds the growth-tools / contacts
  // groups, so the Base card shouldn't advertise them as new (only Marketing is
  // genuinely net-new). Groups WITHOUT a key (or whose key the org lacks) always
  // render. A stock org `can()` none of the higher-tier keys, so it sees the
  // full card — the hiding only ever kicks in for an override exception.
  featureKey?: string;
  // Ancestor group label(s) this group SUPERSEDES. Used by withInheritedFeatures
  // (PlanContextPanel) when composing the full feature list for a checkout panel:
  // PLAN_PRESENTATION is layered, but a higher tier sometimes REPLACES an ancestor
  // capability rather than ADDING to it (e.g. Plus's "Marketing Engine, uncapped"
  // replaces Base's "Marketing engine" — Unlimited broadcasts vs the 4-active cap;
  // Pro's "Scale & intelligence" replaces Plus's SMS/fee group). Appending the
  // ancestor group verbatim would then show a stale lower-tier limit beside the
  // tier's own uncapped row — a contradiction on a PURCHASE screen. Listing the
  // superseded ancestor label(s) here makes the composer drop that ancestor group
  // from the inherited list. Match is by exact `label`. Has NO effect on the
  // standalone billing grid (which renders one tier at a time, never composed).
  supersedesGroups?: string[];
};
export type PlanPresentation = {
  name: string;
  tagline: string;
  inheritFrom: string | null; // "Everything in Starter, plus:"
  heroLine: string | null; // the tier's main funnel sell
  // SMS-free variant of heroLine, used by stripSmsFromPresentation() when the
  // `pocketflows_sms` flag is off (heroLine is a single string, not a filterable
  // list, so it needs an explicit alternate rather than a filter). Only set on
  // tiers whose heroLine mentions SMS (PRO). Absent → heroLine is used as-is.
  heroLineNoSms?: string;
  groups: PlanFeatureGroup[];
};

// --- The editable card content --------------------------------------------
// Taglines, hero lines, and feature-group copy from the approved mockup
// (billing-page-mockup.html).
export const PLAN_PRESENTATION: Record<Plan, PlanPresentation> = {
  STARTER: {
    name: 'Starter',
    tagline: 'Collect donations and run your nonprofit — free forever.',
    inheritFrom: null,
    heroLine: null,
    groups: [
      {
        label: 'Collect donations',
        items: [
          { text: 'Fundraiser & Event forms', hl: true },
          { text: 'Hosted pages + Donor portal' },
          { text: 'Recurring & scheduled donations' },
          { text: 'Checkout Bump + Conversion to Monthly Donation' },
        ],
      },
      {
        label: 'Data & intelligence',
        items: [
          { text: 'Unlimited contacts, import/export, tags', hl: true },
          { text: 'Analytics + Dashboard AI assistant' },
          { text: 'GTM / pixel conversion tracking' },
        ],
      },
      {
        label: 'Integrations & team',
        items: [
          { text: 'CRM Integrations, Zapier, and Public API' },
          { text: 'Double the Donation Integration' },
          { text: 'Roles & permissions, tax receipts' },
        ],
      },
    ],
  },
  BASE: {
    name: 'Base',
    tagline: 'The fundraising engine that pays for itself.',
    inheritFrom: 'Starter',
    heroLine: 'Unlocks growth tools and marketing engine',
    groups: [
      {
        // Growth tools are base+ and included in the legacy_starter grandfather
        // bundle — hidden on the Base card for a grandfathered org.
        label: 'Growth tools & embeds',
        featureKey: 'fundraising_bar',
        items: [
          { text: 'Fundraising bar, QR codes, overlays', hl: true },
          { text: 'URL Builder, Add-to-Cart' },
          { text: 'Supporter lists & fundraising flags' },
        ],
      },
      {
        // Marketing is the ONE thing legacy_starter never has — no featureKey so
        // it ALWAYS renders (the genuine net-new unlock for a grandfathered org).
        label: 'Marketing engine',
        items: [
          { text: 'AI email builder + templates', hl: true },
          {
            text: 'Email Broadcasts',
            limit: 'Up to 4 active at once',
          },
          {
            // PE-1860 (2026-07-26): Base gained 250 included SMS credits/mo. The
            // number is engine-derived at render time via smsIncludedTier (never
            // hand-type it in `text` — see PlanFeatureItem.smsIncludedTier); text is
            // the fallback/placeholder only. smsGated so it drops when the
            // pocketflows_sms flag is off (SMS still dark until PF + PostHog toggles).
            text: '250 SMS / mo included',
            hl: true,
            smsGated: true,
            smsIncludedTier: 'BASE',
          },
          { text: 'Contact Segments' },
          { text: 'Automation, journeys & custom domain' },
        ],
      },
      {
        // Contact merging + EoY receipts are base+ and in the legacy bundle —
        // hidden for a grandfathered org.
        label: 'Contacts & support',
        featureKey: 'contact_merging',
        items: [
          { text: 'Contact merging + EoY tax receipts' },
          { text: 'Email support' },
        ],
      },
    ],
  },
  PLUS: {
    name: 'Plus',
    tagline: 'For teams whose marketing has outgrown Base.',
    inheritFrom: 'Base',
    heroLine: 'Uncaps marketing and lowers platform fee',
    groups: [
      {
        label: 'Marketing Engine, uncapped',
        // Replaces Base's capped "Marketing engine" group — Unlimited broadcasts
        // supersedes the 4-active cap, so the inherited Base group must not also
        // render in a Plus/Pro checkout panel (see withInheritedFeatures).
        supersedesGroups: ['Marketing engine'],
        items: [
          { text: 'Unlimited active broadcasts', hl: true, was: '3 on Base' },
          {
            text: '1,500 SMS / mo included',
            hl: true,
            was: '250 on Base', // PE-1860: Base now includes 250 SMS/mo (was none)
            smsGated: true,
            smsIncludedTier: 'PLUS',
          },
        ],
      },
      {
        label: 'Lower fee & integrations',
        items: [
          {
            text: 'Platform fee drops on every covered gift',
            hl: true,
            was: 'higher on Base',
          },
          { text: 'CRM sync: Salesforce, HubSpot, NetSuite' },
        ],
      },
    ],
  },
  PRO: {
    name: 'Pro',
    tagline: 'For scaled programs that need the lowest fee and full intelligence.',
    inheritFrom: 'Plus',
    heroLine: 'Lowest platform fee and the highest included SMS',
    heroLineNoSms: 'Our lowest platform fee',
    groups: [
      {
        label: 'Scale',
        // Replaces Plus's "Marketing Engine, uncapped" group: Pro's SMS allotment
        // supersedes Plus's 1,500 SMS, and broadcasts remain unlimited (the Pro
        // panel's own copy carries the uncapped broadcast story), so showing both
        // Pro's and Plus's SMS rows would contradict on a purchase screen (the
        // exact numbers are engine-derived at render time via smsIncludedTier,
        // not hard-coded here — see PlanFeatureItem.smsIncludedTier above).
        // Base's capped "Marketing engine" is in turn superseded by Plus's
        // group, so it is dropped transitively (union of all supersedesGroups).
        // PE-1851: roadmap features (custom reporting, deeper intelligence) are
        // deliberately NOT listed — the card advertises only built capability.
        supersedesGroups: ['Marketing Engine, uncapped'],
        items: [
          { text: 'Lowest platform fee', hl: true, was: 'higher on Plus' },
          {
            text: '5,000 SMS / mo included',
            hl: true,
            was: '1,500 on Plus',
            smsGated: true,
            smsIncludedTier: 'PRO',
          },
        ],
      },
    ],
  },
  ENTERPRISE: {
    name: 'Enterprise',
    tagline: 'Custom fees and a dedicated success manager.',
    inheritFrom: 'Pro',
    heroLine: null,
    groups: [],
  },
};

// Strip every SMS-advertising bit from a plan presentation, for when the
// `pocketflows_sms` flag is off (PocketFlows v1 ships without SMS). Pure +
// non-destructive: drops `smsGated` items, rewords `textNoSms` items, and swaps
// in `heroLineNoSms`. Flip the flag back on and the original copy returns.
export function stripSmsFromPresentation(
  presentation: PlanPresentation,
): PlanPresentation {
  return {
    ...presentation,
    heroLine: presentation.heroLineNoSms ?? presentation.heroLine,
    groups: presentation.groups.map((g) => ({
      ...g,
      // Drop SMS-only items; reword items that merely mention SMS.
      items: g.items
        .filter((it) => !it.smsGated)
        .map((it) => (it.textNoSms ? { ...it, text: it.textNoSms } : it)),
    })),
  };
}
